import { BREAKPOINTS, Button, Modal, useMedia } from '@manulife/mux';
import JWT from 'jsonwebtoken';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import useAEMContent, { FRAGMENT_MEMBER_EXP, FRAGMENT_MP } from '../../CustomHooks/useAEMContent';
import useShallowEqualSelector from '../../CustomHooks/useShallowEqualSelector';
import { LOGOUT_TYPE } from '../../utils/AppConstants';
import getLogoutUrl from '../../utils/auth/LogoutManager';
import { ErrorMsg } from '../Shared/SharedStyles';
import { RefreshToken } from './ReducerActions/RefreshToken';
import { TerminateCIAMSession } from './ReducerActions/TerminateCIAMSession';

const ONE_SEC_IN_MS = 1000;
const ONE_MIN_IN_SEC = 60;
const ONE_MIN_IN_MS = ONE_SEC_IN_MS * ONE_MIN_IN_SEC;
const TWENTY_MIN_IN_MS = 20 * ONE_MIN_IN_MS;

// Member is warned 5 min before max session timeout, and 1 minute before inactivity timeout
const MAX_SESSION_WARNING_TIME_IN_MIN = 5;
const MAX_SESSION_WARNING_TIME_IN_MS = MAX_SESSION_WARNING_TIME_IN_MIN * ONE_MIN_IN_MS;

const INACTIVE_WARNING_TIME_IN_MIN = 1;
const INACTIVE_WARNING_TIME_IN_MS = INACTIVE_WARNING_TIME_IN_MIN * ONE_MIN_IN_MS;
const INACTIVE_WARNING_TIME_IN_SEC = INACTIVE_WARNING_TIME_IN_MIN * ONE_MIN_IN_SEC;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin: 0 0 5px 0;
    @media (max-width: ${BREAKPOINTS.MAX_768}px) {
        margin: 0;
    }
`;

const ModalHeading = styled.h2`
    font-size: 16px;
    font-weight: 600;
    line-height: 1.5;
    margin: 0 0 8px 0;
    @media (max-width: ${BREAKPOINTS.MAX_768}px) {
        margin: 10px 0 20px 0;
        text-align: left;
    }
`;

const ModalContent = styled.p`
    font-size: 16px;
    font-weight: 300;
    line-height: 1.5;
    margin: 0 0 30px 0;
    @media (max-width: ${BREAKPOINTS.MAX_768}px) {
        margin: 0 0 31px 0;
        text-align: left;
    }
`;

const modalStyleMobile = {
    modalContentStyle: {
        textAlign: 'left',
        padding: '24px 20px 21px 20px',
    },
    buttonLeftWidth: '155px',
    buttonRightWidth: '150px',
    buttonMargin: '5px',
};

const modalStyleDesktop = {
    modalContentStyle: {
        textAlign: 'center',
        padding: '20px 24px 30px 24px',
    },
    buttonWidth: '165px',
    buttonMargin: '15px',
};

const Timeout = ({ children }) => {
    const [showModal, setShowModal] = useState(false);
    const [shouldBeWarned, setShouldBeWarned] = useState(false);
    const [showTimedoutModal, setShowTimedoutModal] = useState(false);
    const [hasBeenActive, setHasBeenActive] = useState(false);
    const [timeLeft, setTimeLeft] = useState(INACTIVE_WARNING_TIME_IN_SEC);
    const [minutesToMaxTimeoutMessage, setMinutesToMaxTimeoutMessage] = useState('5 minutes');
    const [maxSessionReached, setMaxSessionReached] = useState(false);
    const { jwt, isCiamSession, error, ctxQueryString } = useShallowEqualSelector((state) => ({
        jwt: state.Authentication.jwt,
        isCiamSession: state.Authentication.isCiamSession,
        error: state.Errors.tokenUpdate,
        ctxQueryString: state.Context.ctxQueryString,
    }));

    const warningTimer = useRef(null);
    const logoutTimer = useRef(null);

    const signMeOutButtonText = useAEMContent(FRAGMENT_MP.timeoutModal, 'buttonOne');
    const keepMeHereButtonText = useAEMContent(FRAGMENT_MP.timeoutModal, 'buttonTwo');

    const sessionTimeOutHeader = useAEMContent(FRAGMENT_MEMBER_EXP.memberCommon, 'timeoutHeader', {
        replaceText: [
            {
                key: '{{seconds}}',
                value: timeLeft,
            },
        ],
    });
    const sessionTimeOutMessage = useAEMContent(FRAGMENT_MEMBER_EXP.memberCommon, 'timeoutMsg', {
        replaceText: [
            {
                key: '{{seconds}}',
                value: timeLeft,
            },
        ],
    });

    const sessionTimedOutHeader = useAEMContent(FRAGMENT_MEMBER_EXP.memberCommon, 'timedoutHeader');
    const sessionTimedOutMessage = useAEMContent(FRAGMENT_MEMBER_EXP.memberCommon, 'timedoutMsg');

    const maxSessionTimeOutHeader = useAEMContent(FRAGMENT_MP.timeoutModal, 'subHeaderLineOne', {
        replaceText: [
            {
                key: '1 minute',
                value: `${minutesToMaxTimeoutMessage}`,
            },
        ],
    });

    const maxSessionTimeOutMessage = useAEMContent(FRAGMENT_MP.timeoutModal, 'subHeaderLineThree', {
        replaceText: [
            {
                key: '1 minute',
                value: `${minutesToMaxTimeoutMessage}`,
            },
        ],
    });

    const isMobile = useMedia(
        [`(max-width: ${BREAKPOINTS.TABLET_PORTRAIT}px`], // media queries
        [true], // values corresponding to media query
        false // default value
    );
    const dispatch = useDispatch();

    const isMaxSessionApproaching = useMemo(() => {
        if (!isCiamSession) {
            return false;
        }

        // Offset between server time and user's clock
        const offset = parseInt(sessionStorage.getItem('MPROFILE_SESSION_OFFSET'), 10) || 0;

        // Max Session Expiration Time from JWT
        const decodedJWT = JWT.decode(jwt);
        const sessionExpirationEpoch = new Date(decodedJWT?.CIAM?.maxSessionExpirationTime).getTime();

        // Combine to know how many milliseconds until expiration
        const timeToMaximumSession = sessionExpirationEpoch - Date.now() + offset;

        // Session is considered approaching max session length if remaining time is less than 20 min
        return timeToMaximumSession < TWENTY_MIN_IN_MS;
    }, [jwt, isCiamSession]);

    // Determines dynamic messaging for max session warning modal
    useEffect(() => {
        if (isMaxSessionApproaching) {
            const jwtData = JWT.decode(jwt);

            // Offset between server time and user's clock
            const offset = parseInt(sessionStorage.getItem('MPROFILE_SESSION_OFFSET'), 10) || 0;

            // Time remainining before member is logged out
            const timeToExpiry = Math.round((jwtData?.exp * ONE_SEC_IN_MS - Date.now() + offset) / ONE_MIN_IN_MS);

            // Warning modal will display default 5 minutes
            // Unless there are already fewer than 5 min when member arrives or reloads page.
            const minutes = Math.min(MAX_SESSION_WARNING_TIME_IN_MIN, timeToExpiry);

            const message = `${minutes} minute${minutes === 1 ? '' : 's'}`;
            setMinutesToMaxTimeoutMessage(message);
        }
    }, [isMaxSessionApproaching, jwt]);

    // If warning time has come, check if user has been active within this cycle.
    // If yes, refresh the token in the background.
    // If no or if approaching max CIAM session, show the timeout modal instead.
    useEffect(() => {
        if (shouldBeWarned) {
            setShouldBeWarned(false);
            if (hasBeenActive && !isMaxSessionApproaching) {
                dispatch(RefreshToken());
            } else {
                setMaxSessionReached(isMaxSessionApproaching);
                setShowModal(true);
            }
        }
    }, [hasBeenActive, shouldBeWarned, dispatch, isMaxSessionApproaching]);

    // start timer when modal pops
    useEffect(() => {
        const shouldCreateTimer = timeLeft > 0 && showModal && !maxSessionReached;

        // 1 min countdown timer
        const timer = shouldCreateTimer && setInterval(() => setTimeLeft((sec) => sec - 1), ONE_SEC_IN_MS);
        return () => {
            clearInterval(timer);
            setTimeLeft(INACTIVE_WARNING_TIME_IN_SEC);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal, showTimedoutModal]);

    useEffect(() => {
        //show session expire message after 60s
        if (timeLeft === 0) {
            setShowTimedoutModal(true);
        }
    }, [timeLeft]);

    const endSession = async (logoutReason) => {
        const logoutUrl = getLogoutUrl(logoutReason, ctxQueryString);
        if (isCiamSession) {
            await dispatch(TerminateCIAMSession());
        }
        sessionStorage.clear();
        window.location.assign(logoutUrl);
    };

    // Once we detect any kind of user activity, we don't need to continue to check within this cycle
    const onActivity = useCallback(() => {
        setHasBeenActive(true);
        document.removeEventListener('mousemove', onActivity, false);
        document.removeEventListener('mousedown', onActivity, false);
        document.removeEventListener('keypress', onActivity, false);
        document.removeEventListener('touchmove', onActivity, false);
    }, []);

    // When a new JWT is received, start a new cycle.
    // If within a cycle there is user activity, we will refresh the token for the user.
    // After 14 mins, we'll show the popup.
    // After 15 mins, if there is no refresh, we'll expire the session.
    useEffect(() => {
        const IGNORE_TOKEN_EXPIRY = window.__ENV__.IGNORE_TOKEN_EXPIRY === 'true';
        if (!!jwt && !IGNORE_TOKEN_EXPIRY) {
            // Offset between server time and user's clock
            const offset = parseInt(sessionStorage.getItem('MPROFILE_SESSION_OFFSET'), 10) || 0;
            const jwtData = JWT.decode(jwt);
            const currentTimeStamp = Date.now();
            const timeRemaining = Math.max(0, jwtData.exp * ONE_SEC_IN_MS - currentTimeStamp + offset);

            const logoutReason = isMaxSessionApproaching ? LOGOUT_TYPE.maxSession : LOGOUT_TYPE.idle;
            const warningTime = isMaxSessionApproaching ? MAX_SESSION_WARNING_TIME_IN_MS : INACTIVE_WARNING_TIME_IN_MS;

            //clear any existing timer from previous refresh
            clearTimeout(warningTimer.current);
            clearTimeout(logoutTimer.current);
            setHasBeenActive(false);

            if (!isMaxSessionApproaching) {
                document.addEventListener('mousemove', onActivity, false);
                document.addEventListener('mousedown', onActivity, false);
                document.addEventListener('keypress', onActivity, false);
                document.addEventListener('touchmove', onActivity, false);
            }

            warningTimer.current = setTimeout(() => {
                setShouldBeWarned(true);
            }, timeRemaining - warningTime);
            logoutTimer.current = setTimeout(() => {
                endSession(logoutReason);
                setShowTimedoutModal(true);
            }, timeRemaining);
        }

        return () => {
            clearTimeout(warningTimer.current);
            clearTimeout(logoutTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jwt, onActivity]);

    return (
        <React.Fragment>
            {showTimedoutModal ? (
                <Modal
                    id="mp-timedout-modal"
                    aria={{
                        ariaLabel: `dialog Session Timeout`,
                    }}
                    isOpen={showModal}
                    ariaLabelledBy="mp-timdeout-modal-header"
                    ariaDescribedBy="timedoutModalContent"
                    customStyle={{
                        modalContentStyle: isMobile
                            ? modalStyleMobile.modalContentStyle
                            : modalStyleDesktop.modalContentStyle,
                    }}
                >
                    <ModalHeading id="mp-timedout-modal-header">{sessionTimedOutHeader}</ModalHeading>
                    <ModalContent id="timedoutModalContent">{sessionTimedOutMessage}</ModalContent>
                </Modal>
            ) : (
                <Modal
                    id="mp-timeout-modal"
                    aria={{
                        ariaLabel: `dialog Session Timeout`,
                    }}
                    isOpen={showModal}
                    ariaLabelledBy="mp-timeout-modal-header"
                    ariaDescribedBy="timeoutModalContent"
                    onClose={() => {
                        if (!isMaxSessionApproaching) {
                            dispatch(RefreshToken());
                        }
                        setShowModal(false);
                    }}
                    customStyle={{
                        modalContentStyle: isMobile
                            ? modalStyleMobile.modalContentStyle
                            : modalStyleDesktop.modalContentStyle,
                    }}
                >
                    <ModalHeading id="mp-timeout-modal-header">
                        {maxSessionReached ? maxSessionTimeOutHeader : sessionTimeOutHeader}
                    </ModalHeading>
                    {error?.hasError && (
                        <ErrorMsg>
                            {error.message} Code: {error.code}
                        </ErrorMsg>
                    )}
                    <ModalContent id="timeoutModalContent">
                        {maxSessionReached ? maxSessionTimeOutMessage : sessionTimeOutMessage}
                    </ModalContent>
                    {maxSessionReached ? (
                        <></>
                    ) : (
                        <ButtonWrapper>
                            <Button
                                id="mp-timeout-button-stay"
                                onClick={() => {
                                    dispatch(RefreshToken());
                                    setShowModal(false);
                                }}
                                customStyle={{
                                    buttonStyle: {
                                        width: isMobile
                                            ? modalStyleMobile.buttonLeftWidth
                                            : modalStyleDesktop.buttonWidth,
                                        minWidth: 'auto',
                                        height: 'auto',
                                        marginRight: isMobile
                                            ? modalStyleMobile.buttonMargin
                                            : modalStyleDesktop.buttonMargin,
                                        padding: '10px',
                                        fontSize: '19px',
                                        fontWeight: 'bold',
                                        transform: 'none',
                                    },
                                }}
                            >
                                {keepMeHereButtonText}
                            </Button>
                            <Button
                                id="mp-timeout-button-logout"
                                onClick={() => endSession(LOGOUT_TYPE.signout)}
                                variant={Button.VARIANT.SECONDARY}
                                customStyle={{
                                    buttonStyle: {
                                        width: isMobile
                                            ? modalStyleMobile.buttonRightWidth
                                            : modalStyleDesktop.buttonWidth,
                                        minWidth: 'auto',
                                        height: 'auto',
                                        marginLeft: isMobile
                                            ? modalStyleMobile.buttonMargin
                                            : modalStyleDesktop.buttonMargin,
                                        padding: '10px',
                                        fontSize: '19px',
                                        fontWeight: 'bold',
                                        transform: 'none',
                                    },
                                }}
                            >
                                {signMeOutButtonText}
                            </Button>
                        </ButtonWrapper>
                    )}
                </Modal>
            )}
            {children}
        </React.Fragment>
    );
};

export default Timeout;
